const BASE_URL = 'https://app.greetai.co';

export function generateOgImageUrl(params: {
  title: string;
  companyName: string;
  companyLogo?: string;
  tagline?: string;
}): string {
  const searchParams = new URLSearchParams();
  searchParams.set('title', params.title);
  searchParams.set('companyName', params.companyName);
  if (params.companyLogo) searchParams.set('imageUrl', params.companyLogo);
  searchParams.set(
    'website',
    params.tagline || 'Assessment',
  );

  return `${BASE_URL}/api/og?${searchParams.toString()}`;
}

export function generateLearnOgImageUrl(params: {
  title: string;
  description?: string;
  sessions?: number;
  duration?: number;
  coverImageUrl?: string;
}): string {
  const searchParams = new URLSearchParams();
  searchParams.set('title', params.title);
  if (params.description) searchParams.set('description', params.description);
  if (params.sessions) searchParams.set('sessions', String(params.sessions));
  if (params.duration) searchParams.set('duration', String(params.duration));
  if (params.coverImageUrl) searchParams.set('coverImageUrl', params.coverImageUrl);
  return `${BASE_URL}/api/og-learn?${searchParams.toString()}`;
}

export function createLearnPageHead(params: {
  name: string;
  description?: string;
  coverImageUrl?: string;
  publicId: string;
  sessionCount?: number;
  totalDuration?: number;
}) {
  const url = `${BASE_URL}/learn/${params.publicId}`;
  const title = `${params.name} - GreetAI Learn`;
  const description = params.description || `Learn ${params.name} with interactive AI-powered sessions.`;
  const ogImage = generateLearnOgImageUrl({
    title: params.name,
    description: params.description,
    sessions: params.sessionCount,
    duration: params.totalDuration,
    coverImageUrl: params.coverImageUrl,
  });

  return {
    title,
    meta: [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: ogImage },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:url', content: url },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'GreetAI' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { property: 'twitter:domain', content: 'greetai.co' },
      { property: 'twitter:url', content: url },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage },
    ],
    links: [{ rel: 'canonical', href: url }],
  };
}

export function createApplyPageHead(params: {
  vacancyName: string;
  companyName: string;
  companyLogo?: string;
  publicId: string;
}) {
  const url = `${BASE_URL}/apply/${params.publicId}`;
  const title = `${params.vacancyName} at ${params.companyName} - GreetAI`;
  const description = `Assessment for ${params.vacancyName} at ${params.companyName}.`;
  const ogImage = generateOgImageUrl({
    title: params.vacancyName,
    companyName: params.companyName,
    companyLogo: params.companyLogo,
  });

  return {
    title,
    meta: [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: ogImage },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:url', content: url },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'GreetAI' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { property: 'twitter:domain', content: 'greetai.co' },
      { property: 'twitter:url', content: url },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage },
    ],
    links: [{ rel: 'canonical', href: url }],
  };
}
